import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type RouteTransitionState, type Direction } from './RouteAnimation.types';

const initialState: RouteTransitionState = {
    transitionId: null,
    destination: null,
    direction: 'forward',
    phase: 'idle',
    startTimes: {
        idle: null,
        outgoing: null,
        triggerNavigate: null,
        waitForNavigateComplete: null,
        incoming: null,
    },
};

const routeTransitionSliceInternal = createSlice({
    name: 'routeTransition',
    initialState,
    reducers: {
        startTransition: (
            state,
            action: PayloadAction<{
                transitionId: number;
                destination: string | number;
                direction: Direction;
                now: number;
            }>,
        ) => {
            state.transitionId = action.payload.transitionId;
            state.destination = action.payload.destination;
            state.direction = action.payload.direction;
            state.phase = 'outgoing';
            state.startTimes[state.phase] = action.payload.now;
        },
        completeOutgoingPhase: (state, action: PayloadAction<{ transitionId: number; now: number }>) => {
            if (state.transitionId !== action.payload.transitionId) return;
            state.phase = 'triggerNavigate';
            state.startTimes[state.phase] = action.payload.now;
        },
        navigationTriggered: (state, action: PayloadAction<{ transitionId: number; now: number }>) => {
            if (state.transitionId !== action.payload.transitionId) return;
            state.phase = 'waitForNavigateComplete';
            state.startTimes[state.phase] = action.payload.now;
        },
        beginIncoming: (state, action: PayloadAction<{ transitionId: number; now: number }>) => {
            if (state.transitionId !== action.payload.transitionId) return;
            state.phase = 'incoming';
            state.startTimes[state.phase] = action.payload.now;
        },
        completIncomingPhase: (state, action: PayloadAction<{ transitionId: number; now: number }>) => {
            if (state.transitionId !== action.payload.transitionId) return;
            state.transitionId = null;
            state.destination = null;
            state.phase = 'idle';
            state.startTimes[state.phase] = action.payload.now;
        },
        cancelTransition: (state, action: PayloadAction<{ transitionId: number; now: number }>) => {
            if (state.transitionId !== action.payload.transitionId) return;
            state.transitionId = null;
            state.destination = null;
            state.phase = 'idle';
            state.startTimes[state.phase] = action.payload.now;
        },
    },
});

export const routeTransitionSlice = routeTransitionSliceInternal;
export const routeTransitionActions = routeTransitionSliceInternal.actions;

export const selectRouteTransition = (state: { routeTransition?: RouteTransitionState }) =>
    state.routeTransition ?? initialState;
